import React, { useState, useCallback } from 'react';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { AuthContext } from "./context/auth";
import { GlobalStateContext } from "./context/global_state";
import {LOCAL_STORAGE_KEYS, DQ_THEMES, DQ_ENABLED_THEMES} from "./utils/constants";
import Toast from "./components/UI/Toast/Toast.ui";
import NetworkStatus from './components/NetworkStatus/NetworkStatus.comp';

import createTheme from "./theme/index";
import Routes from './Routes';
import {ThemeProvider} from "styled-components";

const browserHistory = createBrowserHistory();
const App = () => {

	/*
	* ***********
	* Auth Context Data
	* ***********
	*/
	let existingToken;
	let existingUser;
	let existingSettings;
	if (typeof window !== "undefined") {
		existingToken = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN));
		existingUser = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_USER));
		try {
			existingSettings = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_DQ_SETTINGS));
		} catch (e) {
			console.log('FAILED to load the settings from lc-storage, err: ', e);
		}
	}

	const [authToken, setAuthToken] = useState(existingToken);
	const [authUser, setAuthUser] = useState(existingUser);

	const setToken = (data) => {
		localStorage.setItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN, JSON.stringify(data));
		setAuthToken(data);
	}

	const setUser = (data) => {
		localStorage.setItem(LOCAL_STORAGE_KEYS.AUTH_USER, JSON.stringify(data));
		setAuthUser(data);
	}

	/*
	* ***********
	* Global Context Data
	* ***********
	*/

	const initializeProfile = () => {
		const org_code = localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_ORG_CODE);
		if(org_code) {
			const available_profiles = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AVAILABLE_PROFILES));
			if(available_profiles) {
				return available_profiles.find(profile => profile.org_code === org_code);
			}
		}
		return undefined;
	}

	const saveProfile = (profile) => {
		if(profile) {
			const org_code = localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_ORG_CODE);
			const available_profiles = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AVAILABLE_PROFILES));
			if(org_code && available_profiles) {
				const index = available_profiles.findIndex(profile => profile.org_code === org_code);
				if(index !== -1) {
					available_profiles.splice(index, 1, profile);
					localStorage.setItem(LOCAL_STORAGE_KEYS.AVAILABLE_PROFILES, JSON.stringify(available_profiles));
				}
			}
		}
	}

	const [toastShow, _setToastShow] = useState(false);
	const [toastMessage, _setToastMessage] = useState('');
	/*
	 * Which Individual Workspace sections this learner actually has.
	 *
	 * The Navigation Bar sheet tags IW Programs "P1, LC" and Assessments
	 * "P0, P1", but this app carries no plan or solution on the session — only
	 * access_info, which is DQ World / DQ Pro, not a PbDQ package. What it does
	 * have is the learner's own badges, and those are granted BY the package: a
	 * P0 learner is given assessments and no programs, an LC learner the reverse.
	 * So Home publishes what it found here and the sidebar gates on it — the same
	 * visible outcome as package gating, without inventing data the server never
	 * sent. Replace this with the real solution set the day the session carries one.
	 */
	//
	// Kept in localStorage too: a page opened or refreshed away from Home (a
	// course, a quiz) used to show both rows as "Coming soon" until Home loaded
	// again (Yufei-IW-04Nov-01). Logout clears localStorage, so it starts empty.
	const [iwSections, _setIwSections] = useState(() => {
		try {
			return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.IW_SECTIONS)) || { programs: false, assessments: false };
		} catch (e) {
			return { programs: false, assessments: false };
		}
	});
	// Stable, so Home's effect that publishes the sections does not re-run on every render.
	const setIwSections = useCallback((sections) => {
		_setIwSections(sections);
		localStorage.setItem(LOCAL_STORAGE_KEYS.IW_SECTIONS, JSON.stringify(sections));
	}, []);

	const [profile, _setProfile] = useState(() => {
		// Try to initialize profile from localStorage on app start
		const initialProfile = initializeProfile();
		return initialProfile || {'badges': {}, 'org_code': ''};
	});

	const setToastShow = (flag) => {
		_setToastShow(flag);
	}
	const setToastMessage = (message) => {
		_setToastMessage(message);
	}
	const setProfile = (profile) => {
		_setProfile(profile);
		//saveProfile(profile);
	}


	if (!(existingSettings && existingSettings.theme && DQ_ENABLED_THEMES.includes(existingSettings.theme))) {
		existingSettings = {
			theme: DQ_THEMES.DEFAULT
		}
	}
	const [settings, _setSettings] = useState(existingSettings);

	const setSettings = (st_obj) => {
		localStorage.setItem(LOCAL_STORAGE_KEYS.ACTIVE_DQ_SETTINGS, JSON.stringify(st_obj));
		_setSettings(st_obj);
	}
	/*
	* ***********
	* Main App Body
	* ***********
	*/
	return (
		<ThemeProvider theme={createTheme(settings.theme)}>
		<div id="app">
			<NetworkStatus />
			<AuthContext.Provider value={{
				authToken,
				setAuthToken: setToken,
				authUser,
				setAuthUser: setUser
			}}>
				<GlobalStateContext.Provider value={{
					toastShow,
					setToastShow,
					toastMessage,
					setToastMessage,
					profile,
					setProfile,
					settings,
					setSettings,
					iwSections,
					setIwSections,
				}}>
					<Router history={browserHistory}>
						<Routes />
					</Router>
					<Toast />
				</GlobalStateContext.Provider>

			</AuthContext.Provider>

		</div>
		</ThemeProvider>
	);
}

export default App;
